import { useState, useEffect } from 'react';
import { api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { formatDate } from '../lib/utils';
import { turkishIncludes } from '../lib/turkishSearch';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  FileText, 
  Search, 
  Loader2,
  Filter,
  User,
  Building2,
  Settings,
  CheckCircle,
  XCircle,
  LogIn,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';

const actionIcons = {
  USER_REGISTER: UserPlus,
  USER_LOGIN: LogIn,
  USER_APPROVE: CheckCircle,
  USER_REJECT: XCircle,
  USER_ROLE_CHANGE: User,
  FIRM_CREATE: Building2,
  FIRM_UPDATE: Building2,
  FIRM_DELETE: Building2,
  DATABASE_SEED: Settings,
};

const actionColors = {
  USER_REGISTER: 'text-blue-600 bg-blue-50',
  USER_LOGIN: 'text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800',
  USER_APPROVE: 'text-emerald-600 bg-emerald-50',
  USER_REJECT: 'text-red-600 bg-red-50',
  USER_ROLE_CHANGE: 'text-amber-600 bg-amber-50',
  FIRM_CREATE: 'text-indigo-600 bg-indigo-50',
  FIRM_UPDATE: 'text-indigo-600 bg-indigo-50',
  FIRM_DELETE: 'text-red-600 bg-red-50',
  DATABASE_SEED: 'text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800',
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const AuditLogs = () => {
  const { t } = useTranslation();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 0 });

  useEffect(() => {
    setPage(1);
  }, [entityFilter, pageSize]);

  useEffect(() => {
    fetchLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityFilter, page, pageSize]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: pageSize.toString() });
      if (entityFilter !== 'all') params.append('entity_type', entityFilter);
      const response = await api.get(`/audit/logs?${params.toString()}`);
      if (response.data.success) {
        setLogs(response.data.data);
        setPagination(response.data.pagination || { total: 0, total_pages: 0 });
      }
    } catch (error) {
      console.error('Failed to fetch audit logs', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    return (
      turkishIncludes(log.action, searchTerm) ||
      turkishIncludes(log.actor_name, searchTerm) ||
      turkishIncludes(log.entity_type, searchTerm)
    );
  });

  const ActionIcon = ({ action }) => {
    const Icon = actionIcons[action] || FileText;
    const colorClass = actionColors[action] || 'text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800';
    return (
      <div className={`p-2 rounded-lg ${colorClass}`}>
        <Icon className="h-4 w-4" />
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-logs-page">
      <div>
        <h1 
          className="text-2xl font-bold text-slate-900 dark:text-slate-100"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {t('common.auditLogs')}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">{t('admin.auditLogs')}</p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="audit-search-input"
            />
          </div>
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="audit-entity-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by entity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              <SelectItem value="user">Users</SelectItem>
              <SelectItem value="firm">Firms</SelectItem>
              <SelectItem value="system">System</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 dark:text-slate-400">{t('common.noData')}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                  <TableCell>
                    <ActionIcon action={log.action} />
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-sm">
                      {log.action.replace(/_/g, ' ')}
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-600 dark:text-slate-300">
                    {log.actor_name || '-'}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded font-medium">
                      {log.entity_type}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    {log.after_json && (
                      <span className="text-xs text-slate-500 dark:text-slate-400 truncate block">
                        {JSON.stringify(log.after_json).substring(0, 50)}...
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500 dark:text-slate-400 text-sm">
                    {formatDate(log.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {!loading && pagination.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-slate-700" data-testid="audit-pagination">
            <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
              <span>Toplam {pagination.total} kayıt</span>
              <Select value={pageSize.toString()} onValueChange={(v) => setPageSize(parseInt(v))}>
                <SelectTrigger className="w-20 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map(s => (
                    <SelectItem key={s} value={s.toString()}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span>/ sayfa</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPage(1)}
                disabled={page <= 1}
                data-testid="audit-page-first"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                data-testid="audit-page-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-3 text-sm font-medium text-slate-700 dark:text-slate-200">
                {page} / {pagination.total_pages}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                disabled={page >= pagination.total_pages}
                data-testid="audit-page-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setPage(pagination.total_pages)}
                disabled={page >= pagination.total_pages}
                data-testid="audit-page-last"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
